import React, { useState } from 'react';
import { navigate } from '@reach/router';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Stack,
  TextField as MuiTextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Switch,
  MenuItem,
  Button as MuiButton
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Button, TextView, TextField } from '@atoms';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { callStartPerformanceReport, callStartCohortReport, callStartMortgageReport, useGenerateNegotiatorReport, materialTheme } from '@particles';
import { BillingAccountSelector } from '@organisms';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';

type ArtifactTab = 'core' | 'cohort' | 'negotiator' | 'mortgage' | 'custom';

// Client performance report versions (Oct 2026 template). Mortgage has its own tab.
type ReportVariant = 'auto' | 'standard' | 'ooh' | 'lettings' | 'sales';
const REPORT_VARIANT_OPTIONS: { value: ReportVariant; label: string }[] = [
  { value: 'auto', label: 'Automatic' },
  { value: 'standard', label: 'Standard' },
  { value: 'ooh', label: 'Out of hours' },
  { value: 'lettings', label: 'Lettings only' },
  { value: 'sales', label: 'Sales only' }
];

interface CreateArtifactDialogProps {
  open: boolean;
  onClose: () => void;
}

type NegotiatorReportType = 'single' | 'multiple';

function TabPanel({
  children,
  value,
  index
}: {
  children: React.ReactNode;
  value: ArtifactTab;
  index: ArtifactTab;
}) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface PasswordSectionProps {
  enabled: boolean;
  password: string;
  onEnabledChange: (v: boolean) => void;
  onPasswordChange: (v: string) => void;
}

function PasswordSection({ enabled, password, onEnabledChange, onPasswordChange }: PasswordSectionProps) {
  return (
    <Box sx={{ mb: 2 }}>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => {
              onEnabledChange(e.target.checked);
              if (!e.target.checked) onPasswordChange('');
            }}
            color="primary"
            size="small"
          />
        }
        label={<TextView variant="body2">Password protect this artifact</TextView>}
      />
      {enabled && (
        <TextField
          label="Password"
          name="artifactPassword"
          type="password"
          value={password}
          fullWidth={true}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="Viewers will need this password to open the link"
        />
      )}
    </Box>
  );
}

const CreateArtifactDialog: React.FC<CreateArtifactDialogProps> = ({ open, onClose }) => {
  const [tab, setTab] = useState<ArtifactTab>('core');

  // Core report state
  const [selectedCore, setSelectedCore] = useState<BillingAccount | null>(null);
  const [startBillingDate, setStartBillingDate] = useState<DateTime | null>(null);
  const [nextBillingDate, setNextBillingDate] = useState<DateTime | null>(null);
  const [cost, setCost] = useState('');
  const [reportTitle, setReportTitle] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [syncContactsBeforeReport, setSyncContactsBeforeReport] = useState(true);
  const [reportVariant, setReportVariant] = useState<ReportVariant>('auto');
  const [aiPersona, setAiPersona] = useState('');
  const [isSubmittingCore, setIsSubmittingCore] = useState(false);
  const [corePasswordEnabled, setCorePasswordEnabled] = useState(false);
  const [corePassword, setCorePassword] = useState('');

  // Cohort report state
  const [cohortStartDate, setCohortStartDate] = useState<DateTime | null>(null);
  const [cohortEndDate, setCohortEndDate] = useState<DateTime | null>(null);
  const [cohortCost, setCohortCost] = useState('');
  const [cohortTitle, setCohortTitle] = useState('');
  const [cohortDescription, setCohortDescription] = useState('');
  const [cohortSelectedLocations, setCohortSelectedLocations] = useState<BillingAccount[]>([]);
  const [cohortBusinessType, setCohortBusinessType] = useState<string>('all');
  const [cohortPasswordEnabled, setCohortPasswordEnabled] = useState(false);
  const [cohortPassword, setCohortPassword] = useState('');
  // Whether the cohort job should sync each in-scope location's contacts
  // before generating the report. Defaults to true — same as the core /
  // perf-report tab so first-time users get fresh data by default.
  const [cohortSyncContactsBeforeReport, setCohortSyncContactsBeforeReport] = useState(true);

  // Negotiator report state
  const [reportType, setReportType] = useState<NegotiatorReportType>('single');
  const [selectedNegotiator, setSelectedNegotiator] = useState<BillingAccount | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<DateTime | null>(null);
  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [endDate, setEndDate] = useState<DateTime | null>(null);
  const [negotiatorPasswordEnabled, setNegotiatorPasswordEnabled] = useState(false);
  const [negotiatorPassword, setNegotiatorPassword] = useState('');

  // Mortgage report state (mirrors core; backend not wired yet)
  const [mortgageSelectedCore, setMortgageSelectedCore] = useState<BillingAccount | null>(null);
  const [mortgageStartBillingDate, setMortgageStartBillingDate] = useState<DateTime | null>(null);
  const [mortgageNextBillingDate, setMortgageNextBillingDate] = useState<DateTime | null>(null);
  const [mortgageCost, setMortgageCost] = useState('');
  const [mortgageReportTitle, setMortgageReportTitle] = useState('');
  const [mortgageReportDescription, setMortgageReportDescription] = useState('');
  const [mortgageCsvFile, setMortgageCsvFile] = useState<File | null>(null);
  const [isSubmittingMortgage, setIsSubmittingMortgage] = useState(false);
  const [mortgagePasswordEnabled, setMortgagePasswordEnabled] = useState(false);
  const [mortgagePassword, setMortgagePassword] = useState('');

  const queryClient = useQueryClient();
  const generateNegotiatorReportMutation = useGenerateNegotiatorReport();
  const [isSubmittingCohort, setIsSubmittingCohort] = useState(false);

  const handleTabChange = (_: React.SyntheticEvent, newValue: ArtifactTab) => {
    setTab(newValue);
  };

  const handleClose = () => {
    setTab('core');
    setSelectedCore(null);
    setStartBillingDate(null);
    setNextBillingDate(null);
    setCost('');
    setReportTitle('');
    setReportDescription('');
    setSyncContactsBeforeReport(false);
    setReportVariant('auto');
    setAiPersona('');
    setCohortStartDate(null);
    setCohortEndDate(null);
    setCohortCost('');
    setCohortTitle('');
    setCohortDescription('');
    setCohortSelectedLocations([]);
    setCohortBusinessType('all');
    setReportType('single');
    setSelectedNegotiator(null);
    setSelectedMonth(null);
    setStartDate(null);
    setEndDate(null);
    setCorePasswordEnabled(false);
    setCorePassword('');
    setCohortPasswordEnabled(false);
    setCohortPassword('');
    setCohortSyncContactsBeforeReport(true);
    setNegotiatorPasswordEnabled(false);
    setNegotiatorPassword('');
    setMortgageSelectedCore(null);
    setMortgageStartBillingDate(null);
    setMortgageNextBillingDate(null);
    setMortgageCost('');
    setMortgageReportTitle('');
    setMortgageReportDescription('');
    setMortgageCsvFile(null);
    setIsSubmittingMortgage(false);
    setMortgagePasswordEnabled(false);
    setMortgagePassword('');
    onClose();
  };

  // --- Core report handlers ---
  const handleCoreSelected = (core: BillingAccount | null) => {
    setSelectedCore(core);
    if (core) {
      if (core.subscriptionDate) setStartBillingDate(DateTime.fromISO(core.subscriptionDate));
      if (core.nextSubscriptionDate) setNextBillingDate(DateTime.fromISO(core.nextSubscriptionDate));
      setReportTitle(`${core.companyName} — Performance Dashboard`);
      setReportDescription(`Performance report for ${core.companyName} - AI-powered lead qualification and engagement insights`);
    } else {
      setStartBillingDate(null);
      setNextBillingDate(null);
      setReportTitle('');
      setReportDescription('');
    }
  };

  // --- Mortgage report handlers (UI only; backend wired later) ---
  const handleMortgageSelected = (core: BillingAccount | null) => {
    setMortgageSelectedCore(core);
    if (core) {
      if (core.subscriptionDate) setMortgageStartBillingDate(DateTime.fromISO(core.subscriptionDate));
      if (core.nextSubscriptionDate) setMortgageNextBillingDate(DateTime.fromISO(core.nextSubscriptionDate));
      setMortgageReportTitle(`${core.companyName} — Mortgage Report`);
      setMortgageReportDescription(`Mortgage report for ${core.companyName} - AI-powered lead qualification and engagement insights`);
    } else {
      setMortgageStartBillingDate(null);
      setMortgageNextBillingDate(null);
      setMortgageReportTitle('');
      setMortgageReportDescription('');
    }
  };

  const runGenerateReport = async () => {
    if (!selectedCore || !startBillingDate || !nextBillingDate) return;
    const allocatedToken = selectedCore.totalToken?.toString() || '0';
    const consumeToken = selectedCore.srtValuesBasedSubscription?.toString() || '0';
    const availableToken = ((selectedCore.totalToken || 0) - (selectedCore.srtValuesBasedSubscription || 0)).toString();

    const payload: Parameters<typeof callStartPerformanceReport>[0] = {
      id: `${selectedCore.id}-${Date.now()}`,
      billingAccountId: selectedCore.id,
      locationId: selectedCore.locationId || '',
      startDate: startBillingDate.toISODate() || '',
      endDate: nextBillingDate.toISODate() || '',
      companyName: selectedCore.companyName,
      allocatedToken,
      consumeToken,
      availableToken,
      cost,
      title: reportTitle || `${selectedCore.companyName} — Performance Dashboard`,
      description: reportDescription || `Performance report for ${selectedCore.companyName}`,
      isRefetch: false,
      syncContactsFirst: syncContactsBeforeReport,
      reportVariant,
      aiPersona: aiPersona.trim() || undefined
    };
    if (corePasswordEnabled && corePassword.trim()) {
      payload.password = corePassword;
    }

    setIsSubmittingCore(true);
    try {
      const response = await callStartPerformanceReport(payload);
      if (response.code === 202 && response.data?.jobId) {
        handleClose();
        toast.info("Report started. You'll be notified when it's ready.");
        queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      } else {
        const errMsg = (response.data as { message?: string })?.message || 'Failed to start report';
        toast.error(errMsg);
      }
    } catch (e) {
      toast.error('Failed to start report');
    } finally {
      setIsSubmittingCore(false);
    }
  };

  const handleSubmitCoreReport = () => {
    if (!selectedCore) {
      toast.error('Please select a core location');
      return;
    }
    if (!startBillingDate) {
      toast.error('Please select start billing date');
      return;
    }
    if (!nextBillingDate) {
      toast.error('Please select next billing date');
      return;
    }
    if (nextBillingDate <= startBillingDate) {
      toast.error('End date must be after start date');
      return;
    }
    if (!cost || parseFloat(cost) <= 0) {
      toast.error('Please enter a valid cost');
      return;
    }
    if (corePasswordEnabled && !corePassword.trim()) {
      toast.error('Enter a password or turn off password protection');
      return;
    }
    runGenerateReport();
  };

  // --- Cohort report handlers ---
  const handleSubmitCohortReport = async () => {
    if (!cohortStartDate) {
      toast.error('Please select a start date');
      return;
    }
    if (!cohortEndDate) {
      toast.error('Please select an end date');
      return;
    }
    if (cohortEndDate <= cohortStartDate) {
      toast.error('End date must be after start date');
      return;
    }
    if (!cohortCost || parseFloat(cohortCost) <= 0) {
      toast.error('Please enter a valid cost');
      return;
    }
    if (cohortPasswordEnabled && !cohortPassword.trim()) {
      toast.error('Enter a password or turn off password protection');
      return;
    }

    const hasSelectedLocations = cohortSelectedLocations.length > 0;
    const hasBusinessTypeFilter = cohortBusinessType !== 'all';

    let descriptionDefault = 'Cohort performance report across all SalesRook locations';
    if (hasSelectedLocations) {
      descriptionDefault = `Cohort performance report across ${cohortSelectedLocations.length} selected locations`;
    } else if (hasBusinessTypeFilter) {
      descriptionDefault = `Cohort performance report across all ${cohortBusinessType} locations`;
    }

    const payload: {
      startDate: string;
      endDate: string;
      cost: string;
      title: string;
      description: string;
      locationIds?: string[];
      businessType?: string;
      password?: string;
      syncContactsFirst?: boolean;
    } = {
      startDate: cohortStartDate.toISODate() || '',
      endDate: cohortEndDate.toISODate() || '',
      cost: cohortCost,
      title: cohortTitle || 'SalesRook Cohort Report',
      description: cohortDescription || descriptionDefault,
      syncContactsFirst: cohortSyncContactsBeforeReport
    };

    if (hasSelectedLocations) {
      // User selected specific locations — use those only
      payload.locationIds = cohortSelectedLocations.map(loc => loc.id);
    } else if (hasBusinessTypeFilter) {
      // User selected a business type but no specific locations — backend filters by type
      payload.businessType = cohortBusinessType;
    }
    // Otherwise: no locationIds and no businessType → backend uses all locations

    if (cohortPasswordEnabled && cohortPassword.trim()) {
      payload.password = cohortPassword;
    }

    setIsSubmittingCohort(true);
    try {
      const response = await callStartCohortReport(payload);
      if (response.code === 202 && response.data?.jobId) {
        handleClose();
        toast.info("Cohort report started. You'll be notified when it's ready.");
        queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      } else {
        const errMsg = (response.data as { message?: string })?.message || 'Failed to start cohort report';
        toast.error(errMsg);
      }
    } catch (e) {
      toast.error('Failed to start cohort report');
    } finally {
      setIsSubmittingCohort(false);
    }
  };

  // --- Negotiator report handlers ---
  const handleNegotiatorReportTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newType = event.target.value as NegotiatorReportType;
    setReportType(newType);
    if (newType === 'multiple') {
      setSelectedNegotiator(null);
      setStartDate(null);
      setEndDate(null);
    } else {
      setSelectedMonth(null);
    }
  };

  const handleSubmitNegotiatorReport = () => {
    if (reportType === 'single') {
      if (!selectedNegotiator) {
        toast.error('Please select a negotiator');
        return;
      }
      if (!startDate || !endDate) {
        toast.error('Please select start and end dates');
        return;
      }
      if (endDate <= startDate) {
        toast.error('End date must be greater than start date');
        return;
      }
    } else {
      if (!selectedMonth) {
        toast.error('Please select a month');
        return;
      }
    }
    if (negotiatorPasswordEnabled && !negotiatorPassword.trim()) {
      toast.error('Enter a password or turn off password protection');
      return;
    }

    let requestStartDate: DateTime;
    let requestEndDate: DateTime;
    if (reportType === 'single' && startDate && endDate) {
      requestStartDate = startDate;
      requestEndDate = endDate;
    } else if (reportType === 'multiple' && selectedMonth) {
      requestStartDate = selectedMonth.startOf('month');
      requestEndDate = selectedMonth.endOf('month');
    } else {
      return;
    }

    const payload: { companyId?: string; startDate: string; endDate: string; password?: string } = {
      startDate: requestStartDate.toISODate() || '',
      endDate: requestEndDate.toISODate() || ''
    };
    if (reportType === 'single' && selectedNegotiator) payload.companyId = selectedNegotiator.reapitCompanyId;
    if (negotiatorPasswordEnabled && negotiatorPassword.trim()) {
      payload.password = negotiatorPassword;
    }

    generateNegotiatorReportMutation.mutate(payload, {
      onSuccess: (response: unknown) => {
        const data = (response as { data?: { jobIds?: string[]; totalCompanies?: number } })?.data;
        if (reportType === 'multiple' && data?.totalCompanies) {
          toast.info(`${data.totalCompanies} report(s) queued. You'll be notified as they complete.`);
        } else {
          toast.success('Negotiator report generated successfully');
        }
        handleClose();
      },
      onError: (error: unknown) => {
        const errMsg = error && typeof error === 'object' && 'message' in error ? String((error as { message: string }).message) : 'Failed to generate report';
        toast.error(errMsg);
      }
    });
  };

  // --- Mortgage report handler ---
  const handleSubmitMortgageReport = async () => {
    if (!mortgageSelectedCore) {
      toast.error('Please select a mortgage client');
      return;
    }
    if (!mortgageCsvFile) {
      toast.error('Please upload the client CSV export');
      return;
    }
    if (mortgagePasswordEnabled && !mortgagePassword.trim()) {
      toast.error('Enter a password or turn off password protection');
      return;
    }

    setIsSubmittingMortgage(true);
    try {
      const response = await callStartMortgageReport({
        file: mortgageCsvFile,
        companyName: mortgageSelectedCore.companyName,
        locationId: mortgageSelectedCore.locationId || undefined,
        startDate: mortgageStartBillingDate ? mortgageStartBillingDate.toISODate() || undefined : undefined,
        endDate: mortgageNextBillingDate ? mortgageNextBillingDate.toISODate() || undefined : undefined,
        cost: mortgageCost || undefined,
        title: mortgageReportTitle || undefined,
        description: mortgageReportDescription || undefined,
        password: mortgagePasswordEnabled && mortgagePassword.trim() ? mortgagePassword : undefined
      });
      if (response.code === 202 && response.data?.jobId) {
        handleClose();
        toast.info("Mortgage report started. You'll be notified when it's ready.");
        queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      } else {
        toast.error('Failed to start mortgage report');
      }
    } catch (e) {
      const errMsg =
        e && typeof e === 'object' && 'error' in e
          ? String((e as { error?: { message?: string } }).error?.message || 'Failed to start mortgage report')
          : 'Failed to start mortgage report';
      toast.error(errMsg);
    } finally {
      setIsSubmittingMortgage(false);
    }
  };

  // --- Custom report: open create page ---
  const handleOpenCustomReport = () => {
    handleClose();
    navigate('/artifacts/create');
  };

  const isNegotiatorPending = generateNegotiatorReportMutation.isPending;
  const canSubmitCore = tab === 'core' && !isSubmittingCore;
  const canSubmitCohort = tab === 'cohort' && !isSubmittingCohort;
  const canSubmitNegotiator = tab === 'negotiator' && !isNegotiatorPending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <DescriptionIcon sx={{ mr: 1, color: materialTheme.palette.primary.main }} />
          <TextView variant="h6" $fontWeight={600}>
            Create artifact
          </TextView>
        </Box>
      </DialogTitle>
      <Tabs value={tab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tab label="Core" value="core" />
        <Tab label="Cohort" value="cohort" />
        <Tab label="Negotiator" value="negotiator" />
        <Tab label="Mortgage report" value="mortgage" />
        <Tab label="Custom report" value="custom" />
      </Tabs>
      <DialogContent dividers>
        <TabPanel value={tab} index="core">
          <Box sx={{ mb: 3 }}>
            <BillingAccountSelector title="Filter by Account" type="c" status="live" caller="core-report" onSelected={handleCoreSelected} />
          </Box>
          <Box sx={{ mb: 3 }}>
            <TextField
              label="Report Title"
              name="reportTitle"
              fullWidth={true}
              value={reportTitle}
              onChange={(e) => setReportTitle(e.target.value)}
              placeholder="e.g., Company Name — Performance Dashboard"
            />
            <MuiTextField
              label="Report Description"
              name="reportDescription"
              fullWidth
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              placeholder="Performance report with AI-powered insights"
              multiline
              rows={3}
              size="small"
              sx={{ mt: 2 }}
            />
          </Box>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <DatePicker
                label="Start Billing Date"
                format="dd/MM/yyyy"
                value={startBillingDate}
                onChange={(v) => setStartBillingDate(v)}
                maxDate={DateTime.now()}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <DatePicker
                label="Next Billing Date (end date)"
                format="dd/MM/yyyy"
                value={nextBillingDate}
                onChange={(v) => setNextBillingDate(v)}
                minDate={startBillingDate ? startBillingDate.plus({ days: 1 }) : undefined}
                maxDate={DateTime.now()}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Box>
          </Stack>
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Cost"
              name="cost"
              type="number"
              required={true}
              fullWidth={false}
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="Enter cost"
            />
          </Box>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <MuiTextField
              select
              label="Report version"
              size="small"
              value={reportVariant}
              onChange={(e) => setReportVariant(e.target.value as ReportVariant)}
              helperText="Automatic: out of hours from the service types, single type when only one discipline has applicants"
              sx={{ flex: 1 }}
            >
              {REPORT_VARIANT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </MuiTextField>
            <MuiTextField
              label="Assistant name"
              size="small"
              value={aiPersona}
              onChange={(e) => setAiPersona(e.target.value)}
              placeholder="e.g. Ava"
              helperText="As the client knows it"
              sx={{ flex: 1 }}
            />
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={syncContactsBeforeReport}
                onChange={(e) => setSyncContactsBeforeReport(e.target.checked)}
                color="primary"
              />
            }
            label={
              <TextView variant="body2">
                Sync contacts before generating report (uses report end date to sync, then generates report)
              </TextView>
            }
            sx={{ mb: 2, alignItems: 'flex-start' }}
          />
          <PasswordSection
            enabled={corePasswordEnabled}
            password={corePassword}
            onEnabledChange={setCorePasswordEnabled}
            onPasswordChange={setCorePassword}
          />
          <TextView variant="caption" color="textSecondary">
            * All fields are required
          </TextView>
        </TabPanel>

        <TabPanel value={tab} index="cohort">
          <Box sx={{ mb: 3 }}>
            <BillingAccountSelector
              type="c"
              multiple
              status="live"
              showBusinessTypeFilter
              caller="cohort-report"
              title="Select Locations (leave empty for all)"
              onSelected={(cores) => setCohortSelectedLocations(cores)}
              onBusinessTypeChange={(bt) => setCohortBusinessType(bt)}
            />
            <TextView variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              {cohortSelectedLocations.length > 0
                ? `${cohortSelectedLocations.length} location(s) selected`
                : cohortBusinessType !== 'all'
                  ? `No locations selected — report will include all ${cohortBusinessType} locations`
                  : 'No locations selected — report will include all locations'}
            </TextView>
          </Box>
          <Box sx={{ mb: 3 }}>
            <TextField
              label="Report Title"
              name="cohortTitle"
              fullWidth={true}
              value={cohortTitle}
              onChange={(e) => setCohortTitle(e.target.value)}
              placeholder="e.g., SalesRook Cohort Report"
            />
            <MuiTextField
              label="Report Description"
              name="cohortDescription"
              fullWidth
              value={cohortDescription}
              onChange={(e) => setCohortDescription(e.target.value)}
              placeholder="Cohort performance report across all locations"
              multiline
              rows={3}
              size="small"
              sx={{ mt: 2 }}
            />
          </Box>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <DatePicker
                label="Start Date"
                format="dd/MM/yyyy"
                value={cohortStartDate}
                onChange={(v) => setCohortStartDate(v)}
                maxDate={DateTime.now()}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <DatePicker
                label="End Date"
                format="dd/MM/yyyy"
                value={cohortEndDate}
                onChange={(v) => setCohortEndDate(v)}
                minDate={cohortStartDate ? cohortStartDate.plus({ days: 1 }) : undefined}
                maxDate={DateTime.now()}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Box>
          </Stack>
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Cost"
              name="cohortCost"
              type="number"
              required={true}
              fullWidth={false}
              value={cohortCost}
              onChange={(e) => setCohortCost(e.target.value)}
              placeholder="Enter total cost"
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={cohortSyncContactsBeforeReport}
                onChange={(e) => setCohortSyncContactsBeforeReport(e.target.checked)}
                color="primary"
              />
            }
            label={
              <TextView variant="body2">
                Sync contacts for every location in the cohort before generating the report (uses the report window as the sync range)
              </TextView>
            }
            sx={{ mb: 2, alignItems: 'flex-start' }}
          />
          <PasswordSection
            enabled={cohortPasswordEnabled}
            password={cohortPassword}
            onEnabledChange={setCohortPasswordEnabled}
            onPasswordChange={setCohortPassword}
          />
          <TextView variant="caption" color="textSecondary">
            * Date and cost fields are required. Select specific locations or leave empty to include all.
          </TextView>
        </TabPanel>

        <TabPanel value={tab} index="negotiator">
          <Box sx={{ mb: 3 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                Report type
              </FormLabel>
              <RadioGroup row value={reportType} onChange={handleNegotiatorReportTypeChange}>
                <FormControlLabel value="single" control={<Radio />} label="Single (selected company)" />
                <FormControlLabel value="multiple" control={<Radio />} label="Multiple (all companies)" />
              </RadioGroup>
            </FormControl>
          </Box>
          {reportType === 'single' && (
            <Box sx={{ mb: 3 }}>
              <BillingAccountSelector title="Select Account" type="n" status="live" caller="negotiator-report" onSelected={setSelectedNegotiator} />
            </Box>
          )}
          {reportType === 'single' ? (
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(v) => setStartDate(v)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(v) => setEndDate(v)}
                minDate={startDate ? startDate.plus({ days: 1 }) : undefined}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Stack>
          ) : (
            <Box sx={{ mb: 3 }}>
              <DatePicker
                label="Select month"
                views={['year', 'month']}
                openTo="month"
                format="MMMM yyyy"
                value={selectedMonth}
                onChange={(v) => setSelectedMonth(v)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Box>
          )}
          <PasswordSection
            enabled={negotiatorPasswordEnabled}
            password={negotiatorPassword}
            onEnabledChange={setNegotiatorPasswordEnabled}
            onPasswordChange={setNegotiatorPassword}
          />
          <TextView variant="caption" color="textSecondary">
            {reportType === 'single'
              ? '* Select negotiator and date range. Report is generated from database metrics.'
              : '* Select month to generate reports for all companies. The same password applies to every generated report.'}
          </TextView>
        </TabPanel>

        <TabPanel value={tab} index="mortgage">
          <Box sx={{ mb: 3 }}>
            <BillingAccountSelector
              type="c"
              status="live"
              caller="mortgage-report"
              lockBusinessType="mortgage"
              title="Search Mortgage Client"
              onSelected={handleMortgageSelected}
            />
            <TextView variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              Only mortgage clients are listed.
            </TextView>
          </Box>
          <Box sx={{ mb: 3 }}>
            <TextField
              label="Report Title"
              name="mortgageReportTitle"
              fullWidth={true}
              value={mortgageReportTitle}
              onChange={(e) => setMortgageReportTitle(e.target.value)}
              placeholder="e.g., Company Name — Mortgage Report"
            />
            <MuiTextField
              label="Report Description"
              name="mortgageReportDescription"
              fullWidth
              value={mortgageReportDescription}
              onChange={(e) => setMortgageReportDescription(e.target.value)}
              placeholder="Mortgage report with AI-powered insights"
              multiline
              rows={3}
              size="small"
              sx={{ mt: 2 }}
            />
          </Box>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <DatePicker
                label="Start Billing Date"
                format="dd/MM/yyyy"
                value={mortgageStartBillingDate}
                onChange={(v) => setMortgageStartBillingDate(v)}
                maxDate={DateTime.now()}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <DatePicker
                label="Next Billing Date (end date)"
                format="dd/MM/yyyy"
                value={mortgageNextBillingDate}
                onChange={(v) => setMortgageNextBillingDate(v)}
                minDate={mortgageStartBillingDate ? mortgageStartBillingDate.plus({ days: 1 }) : undefined}
                maxDate={DateTime.now()}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Box>
          </Stack>
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Cost"
              name="mortgageCost"
              type="number"
              required={true}
              fullWidth={false}
              value={mortgageCost}
              onChange={(e) => setMortgageCost(e.target.value)}
              placeholder="Enter cost"
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <MuiButton variant="outlined" component="label" startIcon={<UploadFileIcon />} fullWidth>
              {mortgageCsvFile ? mortgageCsvFile.name : 'Upload client CSV export'}
              <input
                type="file"
                hidden
                accept=".csv,text/csv,application/vnd.ms-excel"
                onChange={(e) => setMortgageCsvFile(e.target.files?.[0] || null)}
              />
            </MuiButton>
            <TextView variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              GHL/CRM contact export (.csv). The AI parses this to build the report.
            </TextView>
          </Box>
          <PasswordSection
            enabled={mortgagePasswordEnabled}
            password={mortgagePassword}
            onEnabledChange={setMortgagePasswordEnabled}
            onPasswordChange={setMortgagePassword}
          />
          <TextView variant="caption" color="textSecondary" sx={{ display: 'block' }}>
            * Select a mortgage client and upload the CSV export. Cost and dates add context to the AI report.
          </TextView>
        </TabPanel>

        <TabPanel value={tab} index="custom">
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <TextView variant="body1" sx={{ mb: 2 }}>
              Build a custom HTML report with your own content and upload it as an artifact.
            </TextView>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCustomReport}
              sx={{ height: '45px' }}
            >
              Open custom report builder
            </Button>
          </Box>
        </TabPanel>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        {tab === 'core' && (
          <Button
            variant="contained"
            onClick={() => handleSubmitCoreReport()}
            loading={isSubmittingCore}
            disabled={!canSubmitCore}
            startIcon={<DescriptionIcon />}
          >
            {isSubmittingCore ? 'Starting…' : 'Create report'}
          </Button>
        )}
        {tab === 'cohort' && (
          <Button
            variant="contained"
            onClick={handleSubmitCohortReport}
            loading={isSubmittingCohort}
            disabled={!canSubmitCohort}
            startIcon={<DescriptionIcon />}
          >
            {isSubmittingCohort ? 'Starting…' : 'Create cohort report'}
          </Button>
        )}
        {tab === 'negotiator' && (
          <Button
            variant="contained"
            onClick={handleSubmitNegotiatorReport}
            loading={isNegotiatorPending}
            disabled={!canSubmitNegotiator}
            startIcon={<DescriptionIcon />}
          >
            Create report
          </Button>
        )}
        {tab === 'mortgage' && (
          <Button
            variant="contained"
            onClick={handleSubmitMortgageReport}
            loading={isSubmittingMortgage}
            disabled={isSubmittingMortgage}
            startIcon={<DescriptionIcon />}
          >
            {isSubmittingMortgage ? 'Starting…' : 'Create report'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateArtifactDialog;
